import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  DocumentArrowUpIcon,
  TableCellsIcon,
  CodeBracketIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const fileTypes = [
  { value: "pdf", label: "PDF" },
  { value: "doc", label: "DOC" },
  { value: "docx", label: "DOCX" },
  { value: "txt", label: "TXT" },
  { value: "jpg", label: "JPG" },
  { value: "png", label: "PNG" },
  { value: "zip", label: "ZIP" },
];

const CreateAssignment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [activeQuestionType, setActiveQuestionType] =
    useState("multiple-choice");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    courseId: "",
    type: "homework",
    totalPoints: 100,
    dueDate: "",
    isPublished: false,
    allowLateSubmission: false,
    latePenalty: 0,
    submissionType: "file",
    allowedFileTypes: ["pdf", "doc", "docx"],
    maxFileSize: 10 * 1024 * 1024,
    rubric: [],
    quizSettings: {
      randomizeQuestions: false,
      timeLimit: null,
      maxAttempts: 1,
      autoGrade: true,
    },
    questions: [],
  });

  useEffect(() => {
    const fetchInstructorCourses = async () => {
      try {
        const response = await axios.get(`/api/courses/instructor/${user._id}`);
        setCourses(response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Failed to load courses");
      }
    };

    fetchInstructorCourses();
  }, [user._id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? parseInt(value) || 0
          : value,
    }));
  };

  const handleQuizSettingChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      quizSettings: {
        ...prev.quizSettings,
        [field]: value,
      },
    }));
  };

  // FileTypeChange
  const handleFileTypeChange = (fileType, checked) => {
    setFormData((prev) => ({
      ...prev,
      allowedFileTypes: checked
        ? [...prev.allowedFileTypes, fileType]
        : prev.allowedFileTypes.filter((type) => type !== fileType),
    }));
  };

  // AttachmentChange
  const handleAttachmentChange = (e) => {
    setAttachments(Array.from(e.target.files));
  };

  // Rubric functions
  const addRubricCriteria = () => {
    setFormData((prev) => ({
      ...prev,
      rubric: [...prev.rubric, { criterion: "", value: 0, description: "" }],
    }));
  };

  const removeRubricCriteria = (index) => {
    setFormData((prev) => ({
      ...prev,
      rubric: prev.rubric.filter((_, i) => i !== index),
    }));
  };

  const updateRubricCriteria = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rubric: prev.rubric.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === "value" ? parseInt(value) || 0 : value,
            }
          : item
      ),
    }));
  };

  // Question functions
  const addQuestion = (type) => {
    const newQuestion = {
      questionText: "",
      type: type,
      points: 1,
      explanation: "",
      difficulty: "easy",
      tags: [],
    };

    if (type === "multiple-choice") {
      newQuestion.options = ["", "", "", ""];
      newQuestion.correctOption = 0;
    } else {
      newQuestion.expectedAnswer = "";
    }

    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const handleQuestionTypeChange = (type) => {
    setActiveQuestionType(type);
    // Reset questions when switching type, but keep first question if it exists
    if (formData.questions.length > 0) {
      const firstQuestion = formData.questions[0];
      // Only keep if same type, otherwise clear
      if (firstQuestion.type !== type) {
        setFormData((prev) => ({ ...prev, questions: [] }));
      }
    }
  };

  const removeQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      }),
    }));
  };

  const addQuestionOption = (questionIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, options: [...q.options, ""] } : q
      ),
    }));
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex && q.options.length > 2) {
          return {
            ...q,
            options: q.options.filter((_, oi) => oi !== optionIndex),
            correctOption:
              q.correctOption >= optionIndex && q.correctOption > 0
                ? q.correctOption - 1
                : q.correctOption,
          };
        }
        return q;
      }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate due date
    if (!formData.dueDate) {
      toast.error("Please select a due date");
      setLoading(false);
      return;
    }

    const dueDate = new Date(formData.dueDate);
    if (isNaN(dueDate.getTime()) || dueDate <= new Date()) {
      toast.error("Due date must be in the future");
      setLoading(false);
      return;
    }

    // Validate quiz/exam has questions
    if (
      (formData.type === "quiz" || formData.type === "exam") &&
      formData.questions.length === 0
    ) {
      toast.error("Quiz/Exam must have at least one question");
      setLoading(false);
      return;
    }

    // Validate questions
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.questionText.trim()) {
        toast.error(`Question ${i + 1}: Question text is required`);
        setLoading(false);
        return;
      }
      if (q.type === "multiple-choice") {
        if (q.options.length < 2) {
          toast.error(`Question ${i + 1}: At least 2 options required`);
          setLoading(false);
          return;
        }
        if (q.options.some((opt) => !opt.trim())) {
          toast.error(`Question ${i + 1}: All options must be filled`);
          setLoading(false);
          return;
        }
      } else if (q.type === "written" && !q.expectedAnswer.trim()) {
        toast.error(`Question ${i + 1}: Expected answer is required`);
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        courseId: formData.course,
        instructor: user._id,
        dueDate: dueDate.toISOString(),
      };
      delete payload.course;

      const response = await axios.post("/api/assignments", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      toast.success("Assignment created successfully!");
      navigate(`/assignments/${response.data.assignment._id}`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create assignment"
      );
    } finally {
      setLoading(false);
    }
  };

  // Get minimum datetime (current time + 1 hour)
  const getMinDateTime = () => {
    const minTime = new Date();
    minTime.setHours(minTime.getHours() + 1);
    return minTime.toISOString().slice(0, 16);
  };

  const isQuizOrExam = formData.type === "quiz" || formData.type === "exam";

  return (
    <>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Create Assignment
          </h1>
          <p className="mt-2 text-gray-600">
            Create a new assignment for your students
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Basic Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Title *
                </label>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  maxLength={100}
                  className="input"
                  placeholder="e.g., Data Structures Homework 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course *
                </label>
                <select
                  name="course"
                  required
                  value={formData.course}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.title} ({course.courseCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Type *
                </label>
                <select
                  name="type"
                  required
                  value={formData.type}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="homework">Homework</option>
                  <option value="quiz">Quiz</option>
                  <option value="exam">Exam</option>
                  <option value="project">Project</option>
                  <option value="presentation">Presentation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Points *
                </label>
                <input
                  type="number"
                  name="totalPoints"
                  required
                  min="1"
                  value={formData.totalPoints}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date *
                </label>
                <input
                  type="datetime-local"
                  name="dueDate"
                  required
                  min={getMinDateTime()}
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Assignment must be due at least 1 hour from now
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  required
                  rows={4}
                  maxLength={2000}
                  value={formData.description}
                  onChange={handleChange}
                  className="input"
                  placeholder="Describe the assignment objectives and requirements..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.description.length}/2000 characters
                </p>
              </div>
            </div>

            {/* Submission Settings */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">
                Submission Settings
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      Allow Late Submissions
                    </label>
                    <p className="text-xs text-gray-500">
                      Students can submit after the due date
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    name="allowLateSubmission"
                    checked={formData.allowLateSubmission}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary-600 h-5 w-5"
                  />
                </div>

                {formData.allowLateSubmission && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Late Penalty (% per day)
                    </label>
                    <input
                      type="number"
                      name="latePenalty"
                      min="0"
                      max="100"
                      value={formData.latePenalty}
                      onChange={handleChange}
                      className="input max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deduct this percentage per day late (0 = no penalty)
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      Publish Immediately
                    </label>
                    <p className="text-xs text-gray-500">
                      Make visible to students right away
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary-600 h-5 w-5"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Quiz/Exam Settings - Only show for quiz or exam types */}
          {isQuizOrExam && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AcademicCapIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Quiz/Exam Settings
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Limit (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quizSettings.timeLimit || ""}
                    onChange={(e) =>
                      handleQuizSettingChange(
                        "timeLimit",
                        e.target.value ? parseInt(e.target.value) : null
                      )
                    }
                    className="input"
                    placeholder="No time limit"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank for no time limit
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Attempts
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quizSettings.maxAttempts}
                    onChange={(e) =>
                      handleQuizSettingChange(
                        "maxAttempts",
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="input"
                  />
                </div>

                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-900">
                        Randomize Questions
                      </label>
                      <p className="text-xs text-gray-500">
                        Show questions in random order
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.quizSettings.randomizeQuestions}
                      onChange={(e) =>
                        handleQuizSettingChange(
                          "randomizeQuestions",
                          e.target.checked
                        )
                      }
                      className="rounded border-gray-300 text-primary-600 h-5 w-5"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-900">
                        Auto Grade
                      </label>
                      <p className="text-xs text-gray-500">
                        Automatically grade multiple choice questions
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.quizSettings.autoGrade}
                      onChange={(e) =>
                        handleQuizSettingChange("autoGrade", e.target.checked)
                      }
                      className="rounded border-gray-300 text-primary-600 h-5 w-5"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Questions - Only show for quiz or exam types */}
          {isQuizOrExam && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Questions
                </h2>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleQuestionTypeChange("multiple-choice")}
                    className={`btn btn-sm flex items-center transition-colors ${
                      activeQuestionType === "multiple-choice"
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "btn-secondary"
                    }`}
                  >
                    <AcademicCapIcon className="h-4 w-4 mr-1" />
                    MCQ
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuestionTypeChange("written")}
                    className={`btn btn-sm flex items-center transition-colors ${
                      activeQuestionType === "written"
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "btn-secondary"
                    }`}
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-1" />
                    Written
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBulkUpload(true)}
                    className="btn btn-secondary btn-sm flex items-center"
                  >
                    <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                    Bulk Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      toast("Template download functionality coming soon!", {
                        icon: (
                          <ArrowDownTrayIcon className="h-10 w-10 text-blue-500" />
                        ),
                      });
                    }}
                    className="btn btn-secondary btn-sm flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download Template
                  </button>
                </div>
              </div>

              {formData.questions.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                  <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                  <p className="font-medium">No questions added yet</p>
                  <p className="text-sm mt-1">
                    Select question type above and click "Add Question" to start
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.questions
                    .filter((q) => q.type === activeQuestionType)
                    .map((question, qIndex) => (
                      <div
                        key={qIndex}
                        className="border border-gray-300 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Question {qIndex + 1}
                            <span className="ml-2 text-xs font-normal text-gray-500">
                              (
                              {question.type === "multiple-choice"
                                ? "Multiple Choice"
                                : "Written Answer"}
                              )
                            </span>
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeQuestion(qIndex)}
                            className="btn btn-danger btn-sm"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Question Text */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Question Text *
                          </label>
                          <textarea
                            rows={3}
                            value={question.questionText}
                            onChange={(e) =>
                              updateQuestion(
                                qIndex,
                                "questionText",
                                e.target.value
                              )
                            }
                            className="input"
                            placeholder="Enter your question here..."
                            required
                          />
                        </div>

                        {/* MCQ Options */}
                        {question.type === "multiple-choice" && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Options *
                            </label>
                            <div className="space-y-2">
                              {question.options.map((option, oIndex) => (
                                <div
                                  key={oIndex}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="radio"
                                    name={`question-${qIndex}-correct`}
                                    checked={question.correctOption === oIndex}
                                    onChange={() =>
                                      updateQuestion(
                                        qIndex,
                                        "correctOption",
                                        oIndex
                                      )
                                    }
                                    className="h-4 w-4 text-green-600"
                                    title="Mark as correct answer"
                                  />
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) =>
                                      updateQuestionOption(
                                        qIndex,
                                        oIndex,
                                        e.target.value
                                      )
                                    }
                                    className="input flex-1"
                                    placeholder={`Option ${oIndex + 1}`}
                                    required
                                  />
                                  {question.options.length > 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeQuestionOption(qIndex, oIndex)
                                      }
                                      className="text-red-600 hover:text-red-800 p-1"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() => addQuestionOption(qIndex)}
                              className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              Add Option
                            </button>
                            <p className="text-xs text-gray-500 mt-2">
                              Select the correct answer by clicking the radio
                              button
                            </p>
                          </div>
                        )}

                        {/* Written Answer Expected Answer */}
                        {question.type === "written" && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Student Answer Field (Preview)
                            </label>
                            <textarea
                              rows={4}
                              className="input bg-gray-100"
                              placeholder="Students will type their answer here..."
                              disabled
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              This is a preview of what students will see
                            </p>

                            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                              Expected Answer / Keywords *
                            </label>
                            <textarea
                              rows={3}
                              value={question.expectedAnswer}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "expectedAnswer",
                                  e.target.value
                                )
                              }
                              className="input"
                              placeholder="Provide the expected answer or key points to look for..."
                              required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              This will help with manual grading
                            </p>
                          </div>
                        )}

                        {/* Question Metadata */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Points
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={question.points}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "points",
                                  parseInt(e.target.value) || 1
                                )
                              }
                              className="input"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Difficulty
                            </label>
                            <select
                              value={question.difficulty}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "difficulty",
                                  e.target.value
                                )
                              }
                              className="input"
                            >
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tags (comma separated)
                            </label>
                            <input
                              type="text"
                              value={question.tags.join(", ")}
                              onChange={(e) =>
                                updateQuestion(
                                  qIndex,
                                  "tags",
                                  e.target.value.split(",").map((t) => t.trim())
                                )
                              }
                              className="input"
                              placeholder="e.g., arrays, loops"
                            />
                          </div>
                        </div>

                        {/* Explanation */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Explanation (shown after grading)
                          </label>
                          <textarea
                            rows={2}
                            value={question.explanation}
                            onChange={(e) =>
                              updateQuestion(
                                qIndex,
                                "explanation",
                                e.target.value
                              )
                            }
                            className="input"
                            placeholder="Explain why the correct answer is correct..."
                          />
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {formData.questions.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => addQuestion(activeQuestionType)}
                    className="mt-4 btn btn-primary btn-sm flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Question
                  </button>
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Total Questions:</strong>{" "}
                      {
                        formData.questions.filter(
                          (q) => q.type === activeQuestionType
                        ).length
                      }{" "}
                      (
                      {activeQuestionType === "multiple-choice"
                        ? "MCQ"
                        : "Written"}
                      ) •<strong className="ml-2">Total Points:</strong>{" "}
                      {formData.questions
                        .filter((q) => q.type === activeQuestionType)
                        .reduce((sum, q) => sum + q.points, 0)}
                    </p>
                  </div>
                </>
              )}

              {formData.questions.length === 0 && (
                <button
                  type="button"
                  onClick={() => addQuestion(activeQuestionType)}
                  className="mt-4 btn btn-primary btn-sm flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Question
                </button>
              )}
            </div>
          )}

          {/* Grading Rubric - For non-quiz/exam assignments */}
          {!isQuizOrExam && (
            <>
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Grading Rubric
                  </h2>
                  <button
                    type="button"
                    onClick={addRubricCriteria}
                    className="btn btn-secondary btn-sm flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Criteria
                  </button>
                </div>

                {formData.rubric.length === 0 ? (
                  <p className="text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
                    No rubric criteria added. Click "Add Criteria" to get
                    started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {formData.rubric.map((criteria, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50"
                      >
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Criterion Name *
                          </label>
                          <input
                            type="text"
                            placeholder="e.g., Code Quality"
                            value={criteria.criterion}
                            onChange={(e) =>
                              updateRubricCriteria(
                                index,
                                "criterion",
                                e.target.value
                              )
                            }
                            maxLength={100}
                            className="input"
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Points *
                          </label>
                          <input
                            type="number"
                            placeholder="10"
                            min="0"
                            value={criteria.value}
                            onChange={(e) =>
                              updateRubricCriteria(
                                index,
                                "value",
                                e.target.value
                              )
                            }
                            className="input"
                            required
                          />
                        </div>
                        <div className="md:col-span-5">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            placeholder="Description of criteria"
                            value={criteria.description}
                            onChange={(e) =>
                              updateRubricCriteria(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            maxLength={500}
                            className="input"
                          />
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeRubricCriteria(index)}
                            className="btn btn-danger btn-sm w-full"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {formData.rubric.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Total Rubric Points:</strong>{" "}
                      {formData.rubric.reduce((sum, r) => sum + r.value, 0)}
                    </p>
                  </div>
                )}
              </div>

              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Submission Settings
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Submission Type
                    </label>
                    <select
                      name="submissionType"
                      value={formData.submissionType}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="file">File Upload Only</option>
                      <option value="text">Text Submission Only</option>
                      <option value="both">Both File and Text</option>
                    </select>
                  </div>

                  {formData.submissionType !== "text" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Allowed File Types
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {fileTypes.map((type) => (
                            <label
                              key={type.value}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                checked={formData.allowedFileTypes.includes(
                                  type.value
                                )}
                                onChange={(e) =>
                                  handleFileTypeChange(
                                    type.value,
                                    e.target.checked
                                  )
                                }
                                className="rounded border-gray-300 text-primary-600"
                              />
                              <span className="ml-2 text-sm">{type.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max File Size (MB)
                        </label>
                        <input
                          type="number"
                          name="maxFileSize"
                          min="1"
                          max="100"
                          value={Math.round(formData.maxFileSize / 1024 / 1024)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              maxFileSize:
                                parseInt(e.target.value) * 1024 * 1024,
                            }))
                          }
                          className="input"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Assignment Files
                </h2>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <input
                      type="file"
                      multiple
                      onChange={handleAttachmentChange}
                      className="hidden"
                      id="assignment-files"
                    />
                    <label
                      htmlFor="assignment-files"
                      className="btn btn-secondary cursor-pointer"
                    >
                      Choose Files
                    </label>
                    <p className="text-sm text-gray-600 mt-2">
                      Upload reference materials, templates, or instructions
                    </p>
                  </div>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Selected Files:
                    </h4>
                    <ul className="space-y-1">
                      {attachments.map((file, index) => (
                        <li key={index} className="text-sm text-gray-600">
                          {file.name} ({Math.round(file.size / 1024)}KB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pb-6">
            <button
              type="button"
              onClick={() => navigate("/assignments")}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Creating...
                </>
              ) : (
                "Create Assignment"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Bulk Upload Modal - Rendered outside main container for proper full-screen coverage */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Upload Questions
              </h3>
              <button
                type="button"
                onClick={() => setShowBulkUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Choose a format to upload your questions:
              </p>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    toast("Excel upload functionality coming soon!", {
                      icon: (
                        <InformationCircleIcon className="h-10 w-10 text-blue-500" />
                      ),
                    });
                    setShowBulkUpload(false);
                  }}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center">
                    <TableCellsIcon className="h-8 w-8 text-green-600 mr-3" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">
                        Excel File (.xlsx)
                      </p>
                      <p className="text-xs text-gray-500">
                        Upload questions from Excel spreadsheet
                      </p>
                    </div>
                  </div>
                  <DocumentArrowUpIcon className="h-5 w-5 text-gray-400" />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    toast("JSON upload functionality coming soon!", {
                      icon: (
                        <InformationCircleIcon className="h-10 w-10 text-blue-500" />
                      ),
                    });
                    setShowBulkUpload(false);
                  }}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center">
                    <CodeBracketIcon className="h-8 w-8 text-blue-600 mr-3" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">
                        JSON File (.json)
                      </p>
                      <p className="text-xs text-gray-500">
                        Upload questions from JSON file
                      </p>
                    </div>
                  </div>
                  <DocumentArrowUpIcon className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowBulkUpload(false)}
                className="btn btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateAssignment;
